/**
 * R.H.I.E. Drop Risk Alert Component
 * Kullanıcı bırakma riski uyarı kartı
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, AlertTriangle, TrendingDown, Clock, Play, X, 
  Sparkles, ArrowRight, Shield, Flame
} from 'lucide-react';
import { useRHIE } from './RHIEProvider';
import { DROP_RISK_LEVELS } from '../../services/zihinKilavuzuEventBus';

const riskIcons = {
  LOW: Shield,
  MEDIUM: Clock,
  HIGH: AlertTriangle,
  CRITICAL: Heart
};

const riskMessages = {
  LOW: {
    title: 'Harika gidiyorsun ✧',
    body: 'Ritüellerini düzenli yapıyorsun. Devam ettiğinde… farkı göreceksin.',
    cta: 'Yolculuğunu sürdür'
  },
  MEDIUM: {
    title: 'Nazik bir hatırlatma ◇',
    body: 'Biraz ara vermişsin. Şimdi… bir nefes. Kendine izin ver.',
    cta: 'Kendine izin ver'
  },
  HIGH: {
    title: 'Seni özledik ◇',
    body: 'Uzun süredir görüşemedik. Bugün… dönüşüm adımı. 5 dakika yeter.',
    cta: 'Derin nefes al… ve başla'
  },
  CRITICAL: {
    title: 'Seninle konuşalım ∗',
    body: 'Bu bir kayıp değil… dönüşün başlangıcı. Hazırsan, küçük bir adımla başlayabilirsin.',
    cta: 'Hazırsan adım at'
  }
};

export default function DropRiskAlert({ 
  showWhenLow = false,
  dismissable = true,
  onDismiss,
  className = ''
}) {
  const navigate = useNavigate();
  const { engine, startRitual } = useRHIE();
  const [dismissed, setDismissed] = useState(false);
  
  if (!engine) return null;
  
  const riskLevel = engine.getDropRiskLevel();
  const riskScore = engine.getDropRiskScore();
  const daysSince = engine.getDaysSinceLastActivity();
  const winbackSuggestion = engine.getWinbackSuggestion();
  
  // Don't show if low risk (unless explicitly requested)
  if (riskLevel === 'LOW' && !showWhenLow) return null;
  if (dismissed) return null;
  
  const config = DROP_RISK_LEVELS[riskLevel];
  const message = riskMessages[riskLevel];
  const Icon = riskIcons[riskLevel];
  
  const handleAction = () => {
    if (winbackSuggestion) {
      startRitual(winbackSuggestion.session);
      navigate('/panel/telkinler');
    } else {
      navigate('/panel/telkinler');
    }
  };
  
  const handleDismiss = () => {
    setDismissed(true);
    if (onDismiss) onDismiss();
  };
  
  // Compact view for LOW/MEDIUM
  if (riskLevel === 'LOW' || riskLevel === 'MEDIUM') {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={`relative rounded-xl p-4 ${className}`}
        style={{
          background: `linear-gradient(135deg, ${config.color}15, ${config.color}05)`,
          border: `1px solid ${config.color}30`
        }}
      >
        {dismissable && (
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
          >
            <X className="w-3 h-3 text-white/40" />
          </button>
        )}
        
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: `${config.color}20` }}
          >
            <Icon className="w-5 h-5" style={{ color: config.color }} />
          </div>
          
          <div className="flex-1">
            <p className="text-white/70 text-sm">{message.body}</p>
          </div>
          
          <button
            onClick={handleAction}
            className="flex items-center gap-1 text-sm font-medium transition-colors hover:opacity-80"
            style={{ color: config.color }}
          >
            {message.cta}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    );
  }
  
  // Full card for HIGH/CRITICAL
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`relative overflow-hidden rounded-2xl ${className}`}
      style={{
        background: 'linear-gradient(135deg, rgba(18, 18, 30, 0.9), rgba(18, 18, 30, 0.7))',
        backdropFilter: 'blur(16px)',
        border: `1px solid ${config.color}40`
      }}
    >
      {/* Animated gradient background */}
      <motion.div
        className="absolute inset-0 opacity-30"
        animate={{
          background: [
            `radial-gradient(circle at 20% 20%, ${config.color}30, transparent)`,
            `radial-gradient(circle at 80% 80%, ${config.color}30, transparent)`,
            `radial-gradient(circle at 20% 20%, ${config.color}30, transparent)`
          ]
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      
      {dismissable && (
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors z-10"
        >
          <X className="w-4 h-4 text-white/50" />
        </button>
      )}
      
      <div className="relative p-6">
        {/* Icon */}
        <motion.div
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 3, repeat: Infinity }}
          className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 mx-auto"
          style={{ 
            background: `linear-gradient(135deg, ${config.color}40, ${config.color}20)`,
            boxShadow: `0 10px 40px -10px ${config.color}50`
          }}
        >
          <Icon className="w-8 h-8" style={{ color: config.color }} />
        </motion.div>
        
        {/* Title & Body */}
        <div className="text-center mb-6">
          <h3 className="text-white text-xl font-semibold mb-2">{message.title}</h3>
          <p className="text-white/60 text-sm leading-relaxed">{message.body}</p>
          
          {daysSince > 0 && (
            <div className="mt-3 flex items-center justify-center gap-2 text-white/30 text-xs">
              <Clock className="w-3 h-3" />
              <span>Son aktivite: {daysSince} gün önce</span>
            </div>
          )}
        </div>
        
        {/* Winback suggestion */}
        {winbackSuggestion && (
          <div 
            className="mb-4 p-3 rounded-xl"
            style={{ background: 'rgba(255,255,255,0.03)' }}
          >
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ background: `${config.color}20` }}
              >
                <Sparkles className="w-5 h-5" style={{ color: config.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white/40 text-xs">Önerilen Başlangıç</p>
                <p className="text-white text-sm font-medium truncate">
                  {winbackSuggestion.session?.title}
                </p>
              </div>
              <div className="text-xs text-white/30">
                {winbackSuggestion.session?.duration_display || '5 dk'}
              </div>
            </div>
          </div>
        )}
        
        {/* CTA Button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleAction}
          className="w-full py-4 rounded-xl flex items-center justify-center gap-2 text-white font-semibold transition-all"
          style={{ 
            background: `linear-gradient(135deg, ${config.color}, ${config.color}99)`,
            boxShadow: `0 4px 25px -5px ${config.color}60`
          }}
        >
          <Play className="w-5 h-5" />
          {message.cta}
        </motion.button>
        
        {/* Skip option */}
        <button
          onClick={handleDismiss}
          className="w-full mt-3 py-2 text-white/30 text-sm hover:text-white/50 transition-colors"
        >
          Belki birazdan...
        </button>
      </div>
    </motion.div>
  );
}
